import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { BarChart, LineChart } from '@mui/x-charts';
import { DataGrid } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { requestJson } from '../lib/api';
import { useMenuPage } from '../hooks/useMenuPage';

function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 6);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('fr-FR');
}

function formatHours(value) {
  const number = Number(value || 0);
  if (number < 24) return `${number.toFixed(2)} h`;
  return `${(number / 24).toFixed(2)} j`;
}

function formatPeriodLabel(period) {
  if (!period) return '';
  const [year, month] = String(period).split('-');
  const date = new Date(Number(year), Number(month || 1) - 1, 1);
  if (Number.isNaN(date.getTime())) return period;
  return date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
}

function csvEscape(value) {
  const text = value == null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadCsv(filename, rows) {
  const content = rows.map((row) => row.map(csvEscape).join(';')).join('\n');
  const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function EmptyChart({ label = 'Aucune donnée.' }) {
  return (
    <Box sx={{ height: 280, display: 'grid', placeItems: 'center', color: 'text.secondary' }}>
      {label}
    </Box>
  );
}

const categoryLabels = {
  stock: 'Verre de stock',
  supplier: 'Verre fournisseur',
  workshopFollowup: 'Verre fournisseur',
};

export default function DelayStatisticsPage() {
  const menuPage = useMenuPage('/statistiques/delais');
  const initialRange = useMemo(defaultDateRange, []);
  const [range, setRange] = useState(initialRange);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const query = new URLSearchParams(range).toString();
      setStats(await requestJson(`/statistics/delays/mounting-to-send?${query}`));
    } catch (err) {
      setError(err.message);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const filteredRecords = useMemo(
    () =>
      (stats?.records || []).filter((record) =>
        categoryFilter ? record.category === categoryFilter : true
      ),
    [categoryFilter, stats]
  );

  const gridRows = useMemo(
    () =>
      filteredRecords.map((record) => ({
        ...record,
        id: record.commandeId,
        categoryLabel: categoryLabels[record.category] || record.category,
        storeLabel: [record.storeCode, record.storeName].filter(Boolean).join(' - '),
        workshopLabel: [record.workshopCode, record.workshopName].filter(Boolean).join(' - '),
      })),
    [filteredRecords]
  );

  const timelineData = useMemo(() => {
    const periods = [...new Set((stats?.timeline || []).map((item) => item.period))].sort();
    return periods.map((period) => {
      const periodItems = (stats?.timeline || []).filter((item) => item.period === period);
      return {
        period,
        stock: periodItems.find((item) => item.category === 'stock')?.averageHours || null,
        supplier:
          periodItems.find((item) => item.category === 'supplier')?.averageHours ||
          periodItems.find((item) => item.category === 'workshopFollowup')?.averageHours ||
          null,
      };
    });
  }, [stats]);

  const summaryBarData = useMemo(
    () => [
      {
        label: 'Verre de stock',
        min: stats?.summary?.stock?.minHours || 0,
        average: stats?.summary?.stock?.averageHours || 0,
        max: stats?.summary?.stock?.maxHours || 0,
      },
      {
        label: 'Verre fournisseur',
        min: (stats?.summary?.supplier || stats?.summary?.workshopFollowup)?.minHours || 0,
        average: (stats?.summary?.supplier || stats?.summary?.workshopFollowup)?.averageHours || 0,
        max: (stats?.summary?.supplier || stats?.summary?.workshopFollowup)?.maxHours || 0,
      },
    ],
    [stats]
  );

  const columns = useMemo(
    () => [
      { field: 'jobNumber', headerName: 'JOB', minWidth: 130, flex: 0.7 },
      {
        field: 'categoryLabel',
        headerName: 'Type',
        minWidth: 150,
        flex: 0.8,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value}
            color={params.row.category === 'stock' ? 'primary' : 'secondary'}
            variant="outlined"
          />
        ),
      },
      { field: 'storeLabel', headerName: 'Magasin', minWidth: 240, flex: 1.1 },
      { field: 'mountingType', headerName: 'Type de montage', minWidth: 160, flex: 0.8 },
      {
        field: 'receptionLensAt',
        headerName: 'Réception verre',
        minWidth: 180,
        flex: 0.8,
        valueFormatter: (value) => formatDateTime(value),
      },
      {
        field: 'sentAt',
        headerName: 'Envoi magasin',
        minWidth: 180,
        flex: 0.8,
        valueFormatter: (value) => formatDateTime(value),
      },
      {
        field: 'durationHours',
        headerName: 'Délai',
        minWidth: 120,
        flex: 0.6,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => formatHours(value),
      },
    ],
    []
  );

  const totalSummary = stats?.summary?.all || {};

  const handleExportCsv = () => {
    const rows = [
      [
        'JOB',
        'Type',
        'Magasin',
        'Atelier',
        'Type de montage',
        'Réception verre',
        'Envoi magasin',
        'Délai heures',
      ],
      ...gridRows.map((row) => [
        row.jobNumber || '',
        row.categoryLabel || '',
        row.storeLabel || '',
        row.workshopLabel || '',
        row.mountingType || '',
        formatDateTime(row.receptionLensAt),
        formatDateTime(row.sentAt),
        row.durationHours ?? '',
      ]),
    ];

    downloadCsv(`statistiques-delais-${range.from}-${range.to}.csv`, rows);
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 900 }}>
            {menuPage.sectionTitle}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            {menuPage.pageTitle}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            type="date"
            label="Du"
            value={range.from}
            onChange={(event) => setRange((current) => ({ ...current, from: event.target.value }))}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label="Au"
            value={range.to}
            onChange={(event) => setRange((current) => ({ ...current, to: event.target.value }))}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Type"
            select
            SelectProps={{ native: true }}
            size="small"
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
            sx={{ minWidth: 190 }}
          >
            <option value="">Tous</option>
            <option value="stock">Verre de stock</option>
            <option value="supplier">Verre fournisseur</option>
          </TextField>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadStats}>
            Rafraîchir
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadRoundedIcon />}
            onClick={handleExportCsv}
            disabled={!gridRows.length}
          >
            Export CSV
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <TimerRoundedIcon color="primary" />
                <Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>
                  {totalSummary.count || 0}
                </Typography>
                <Typography color="text.secondary">Dossiers mesurés</Typography>
              </CardContent>
            </Card>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <SpeedRoundedIcon color="success" />
                <Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>
                  {formatHours(totalSummary.averageHours)}
                </Typography>
                <Typography color="text.secondary">Délai moyen</Typography>
              </CardContent>
            </Card>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <HourglassBottomRoundedIcon color="warning" />
                <Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>
                  {formatHours(totalSummary.maxHours)}
                </Typography>
                <Typography color="text.secondary">Délai max</Typography>
              </CardContent>
            </Card>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <TimerRoundedIcon color="secondary" />
                <Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>
                  {formatHours(totalSummary.totalHours)}
                </Typography>
                <Typography color="text.secondary">Temps cumulé</Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
                  Min / moyen / max par type
                </Typography>
                {stats?.summary?.all?.count ? (
                  <BarChart
                    height={330}
                    dataset={summaryBarData}
                    xAxis={[{ scaleType: 'band', dataKey: 'label' }]}
                    yAxis={[{ min: 0, label: 'Heures' }]}
                    series={[
                      { dataKey: 'min', label: 'Min', color: '#22c55e' },
                      { dataKey: 'average', label: 'Moyen', color: '#38bdf8' },
                      { dataKey: 'max', label: 'Max', color: '#f59e0b' },
                    ]}
                    grid={{ horizontal: true }}
                  />
                ) : (
                  <EmptyChart />
                )}
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
                  Évolution du délai moyen
                </Typography>
                {timelineData.length ? (
                  <LineChart
                    height={330}
                    xAxis={[
                      {
                        scaleType: 'point',
                        data: timelineData.map((item) => item.period),
                        valueFormatter: formatPeriodLabel,
                        tickInterval: timelineData.map((item) => item.period),
                        tickLabelInterval: () => true,
                        tickLabelMinGap: 0,
                        height: 58,
                        tickSize: 6,
                        tickLabelStyle: { fontSize: 12, fontWeight: 800, fill: 'currentColor' },
                      },
                    ]}
                    margin={{ bottom: 28 }}
                    yAxis={[{ min: 0, label: 'Heures' }]}
                    series={[
                      {
                        data: timelineData.map((item) => item.stock),
                        label: 'Verre de stock',
                        color: '#38bdf8',
                        showMark: true,
                        connectNulls: true,
                      },
                      {
                        data: timelineData.map((item) => item.supplier),
                        label: 'Verre fournisseur',
                        color: '#a78bfa',
                        showMark: true,
                        connectNulls: true,
                      },
                    ]}
                    grid={{ horizontal: true }}
                    sx={{
                      '& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel': {
                        display: 'block',
                        fill: 'currentColor',
                        opacity: 1,
                      },
                    }}
                  />
                ) : (
                  <EmptyChart label="Aucune donnée temporelle." />
                )}
              </CardContent>
            </Card>
          </Box>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
                Détail par dossier atelier
              </Typography>
              <DataGrid
                rows={gridRows}
                columns={columns}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                pageSizeOptions={[10, 25, 50, 100]}
                disableRowSelectionOnClick
                autoHeight
                sx={{
                  border: 0,
                  '& .MuiDataGrid-columnHeaders': {
                    bgcolor: 'rgba(148, 163, 184, 0.12)',
                  },
                }}
              />
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}
